import {
  Grid,
  makeStyles,
  Button,
  CustomBadge,
  FormSectionTitle,
  HtmlContent,
  Skeleton,
  Typography,
  useTranslation
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { DialogStackContext, QuoteTagChip, useClientOptions } from '@paintscout/ui-business'
import type { QuoteDetails, QuoteItemSection, QuoteItemView, RenderableItem } from '@paintscout/util/builder'
import { localizeDecimal } from '@paintscout/util/builder/util/format-number/format-number'
import {
  generateProductDescription,
  getDetails,
  getObjectLabels,
  getQuoteOptions,
  getTags,
  getTotals,
  setQuoteOptions
} from '@paintscout/util/builder'
import { isEmpty } from '@paintscout/util/util'
import classnames from 'classnames'
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import React, { useContext, useMemo } from 'react'
import AreaSubstrateView from '../../../AreaSubstrateView'
import { useQuote } from '../../../context'
import EditCrewNoteDialog from '../../../dialogs/EditCrewNoteDialog'
import FilesByItem from '../../FilesByItem'
import QuoteHeader from '../../QuoteHeader'
import QuoteHeaderCard from '../../QuoteHeader/QuoteHeaderCard'
import OptionsTable from '../QuoteView/OptionsTable'
import WorkOrderTable from './WorkOrderTable'
import { translateStructuredWorkOrderContent } from './WorkOrderCard/translateStructuredWorkOrderContent'
import { useTranslatedNoteContent } from './useTranslatedNoteContent'

const useStyles = makeStyles<Theme, WorkOrderViewProps>(
  (theme) => ({
    root: {},
    headerWrapper: {},
    note: {
      padding: 0
    },
    noteHover: {
      padding: 0
    },
    hours: {
      padding: 0
    },
    optionsTable: {},
    row: {
      display: 'grid',
      gridTemplateColumns: 'auto auto',
      justifyContent: 'space-between'
    },
    table: {
      '& thead': {
        background: theme.paintscout.palette.brand.Neutral.P94
      },
      '& thead th': {
        color: theme.palette.common.black
      }
    },
    hidePrint: {
      '@media print': {
        display: 'none'
      }
    },
    spacer: {
      marginTop: theme.spacing(5)
    },
    afterHeaderWrapper: {
      display: 'grid',
      gridTemplateColumns: 'auto 270px',
      gridGap: theme.spacing(2)
    },
    internalNote: {
      whiteSpace: 'pre-wrap'
    },
    headerTotals: {
      marginBottom: theme.spacing(2)
    },
    avoidPageBreak: {
      '@media print': {
        pageBreakInside: 'avoid',
        breakInside: 'avoid'
      }
    },
    tags: {
      padding: theme.spacing(1)
    }
  }),
  { name: 'WorkOrderView' }
)

export interface WorkOrderViewProps {
  classes?: StyleClasses<typeof useStyles>
  /**
   * Skip the legacy tags/header/summary-cards block and render only the table sections. Used by the
   * redesigned estimateNext Work Order view, which supplies its own header + summary card and reuses
   * this component purely for the Area/Substrate/Options/Media tables.
   */
  hideHeader?: boolean
  /**
   * Skip the two `bid` Area/Substrate tables (the redesigned view renders those as its own
   * `WorkOrderSectionNext` grids), keeping only Additional Work / Options / Media below.
   */
  hideBidTables?: boolean
  /**
   * Skip the Options block (the redesigned view renders it in-card with the shared `OptionsTableNext`).
   */
  hideOptions?: boolean
  /**
   * Skip the Additional Work tables (the redesigned view renders those in-card with the shared
   * `WorkOrderSectionNext`, alongside the Areas / Surfaces / Options sections).
   */
  hideAdditionalWork?: boolean
}

function WorkOrderView(props: WorkOrderViewProps) {
  const classes = useStyles(props)
  const { hideHeader, hideBidTables, hideOptions, hideAdditionalWork } = props
  const { isEditable, isNoteEditable, quote, updateQuote, onReorder, onItemAction, onAddItem } = useQuote()
  const { options } = useClientOptions()
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const { __, lang } = useTranslation()

  const quoteOptions = getQuoteOptions({ quote, options })
  const {
    showWorkOrderAreas,
    showWorkOrderAreaOptions,
    showWorkOrderSubstrates,
    showWorkOrderAreaValues,
    showWorkOrderTotalHours,
    showWorkOrderSubstrateValues,
    showWorkOrderFiles,
    workOrderOptionsConsumer
  } = quoteOptions

  const details = useMemo(() => getDetails({ quote, options }), [quote, options])
  const objectLabels = getObjectLabels({ options })
  const { internal: internalNote } = details.notes
  const workOrderProductNote = useMemo(() => getProductNote({ quote, options, details }), [quote, options, details])
  const totals = useMemo(() => getTotals({ quote, options, consumer: 'crew' }), [quote, options])
  const [[translatedCrewNote, translatedProductNote], translating] = useTranslatedNoteContent([
    internalNote as string,
    workOrderProductNote
  ])

  const hasTotalDimensions =
    !quoteOptions.hideWorkOrderDimensions &&
    totals.dimensions &&
    (totals.dimensions.lnft > 0 ||
      totals.dimensions.sqft > 0 ||
      totals.dimensions.sqftWalls > 0 ||
      totals.dimensions.sqftCeiling > 0 ||
      totals.dimensions.sqftFloor > 0)

  const handleOptionsViewChange = (_event: React.SyntheticEvent, view: 'crew' | 'customer') => {
    const quoteOptions = getQuoteOptions({ options, quote })

    const updatedQuote = setQuoteOptions({
      quoteOptions: { ...quoteOptions, workOrderOptionsConsumer: view },
      quote,
      options
    })
    updateQuote({ quote: updatedQuote })
  }

  const handleToggleAmount = (dropdownOption: string) => {
    const quoteOptions = getQuoteOptions({ options, quote })
    let trueKeys = []
    let falseKeys = []
    const itemKeys = [
      'showWorkOrderAreaValues',
      'showWorkOrderSubstrateValues',
      'showWorkOrderAreaOptionValues',
      'showWorkOrderSubstrateOptionValues'
    ]
    const totalKeys = ['showWorkOrderTotalHours']
    if (dropdownOption == 'showAllHours') {
      trueKeys = [...itemKeys, ...totalKeys]
    } else if (dropdownOption == 'hideAllHours') {
      falseKeys = [...itemKeys, ...totalKeys]
    } else if (dropdownOption == 'hideItemHours') {
      falseKeys = [...itemKeys]
      trueKeys = [...totalKeys]
    } else if (dropdownOption == 'hideTotalHours') {
      trueKeys = [...itemKeys]
      falseKeys = [...totalKeys]
    }
    trueKeys.forEach((key) => {
      quoteOptions[key] = true
    })
    falseKeys.forEach((key) => {
      quoteOptions[key] = false
    })

    const updatedQuote = setQuoteOptions({ quoteOptions, quote, options })
    updateQuote({ quote: updatedQuote })
  }

  const handleEditNote = (_event: React.MouseEvent<HTMLElement>) => {
    openDialog(EditCrewNoteDialog, {
      quote,
      onConfirm: (updatedQuote: QuoteDocument) => {
        updateQuote({ quote: updatedQuote })

        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  const handleReorder = (args: { items: RenderableItem[]; section: QuoteItemSection; view: QuoteItemView }) => {
    if (onReorder) {
      onReorder(args)
    }
  }

  const handleAdd = (_event: React.MouseEvent<HTMLElement>) => {
    if (onAddItem) {
      onAddItem()
    }
  }

  const handleItemActionClick = (
    _ev: React.MouseEvent | null,
    action: string,
    section: QuoteItemSection,
    key: string
  ) => {
    onItemAction(action, section, [key])
  }

  const handleToggle = (_event: React.SyntheticEvent, name: string) => {
    const quoteOptions = getQuoteOptions({ options, quote })

    quoteOptions[name] = !quoteOptions[name]

    const updatedQuote = setQuoteOptions({ quoteOptions, quote, options })
    updateQuote({ quote: updatedQuote })
  }

  const noteHoverContent = (
    <Button variant={'outlined'} fullWidth={true} classes={{ root: classes.hidePrint }}>
      {__('Edit Crew Note')}
    </Button>
  )
  const productHoverContent = (
    <Button variant={'outlined'} fullWidth={true} classes={{ root: classes.hidePrint }}>
      {__('Edit Product Description')}
    </Button>
  )

  const optionsViewSwitcher = isEditable ? (
    <AreaSubstrateView
      hideOnSmall={true}
      view={workOrderOptionsConsumer}
      onChange={handleOptionsViewChange}
      options={[
        { label: __('Customer/Price'), value: 'customer' },
        { label: __('Crew/Hours'), value: 'crew' }
      ]}
    />
  ) : null

  const hasInternalNote = !isEmpty(internalNote)
  const translatedInternalNote = translateStructuredWorkOrderContent(translatedCrewNote, __)
  const translatedWorkOrderProductNote = translateStructuredWorkOrderContent(translatedProductNote, __)

  const totalHours = details.totals?.hours?.useCustom ? details.totals.hours.custom : details.totals.hours.default
  const showTotalHours = showWorkOrderTotalHours && (isEditable || Number(totalHours) > 0)
  const tags = useMemo(() => (quote.tags ? getTags({ options, keys: quote.tags }) : []), [quote.tags, options])

  return (
    <div className={classes.root} id={'work-order'} data-testid="work-order">
      {!hideHeader && (
        <>
          <Grid item container spacing={1} className={classes.tags}>
            {tags.map((tag) => {
              return (
                <Grid item key={tag.name}>
                  <QuoteTagChip tag={tag} />
                </Grid>
              )
            })}
          </Grid>
          <div className={classes.headerWrapper}>
            <QuoteHeader isEditable={isEditable} variant="work-order" />
          </div>
          <Grid container={true} spacing={3} classes={{ root: classes.headerTotals }}>
            <Grid item={true} xs={12} sm={8} className={classes.avoidPageBreak}>
              {(hasInternalNote || isEditable || isNoteEditable) && (
                <QuoteHeaderCard
                  title={__('Crew Note')}
                  classes={{ contents: classes.note, placeholderContent: classes.noteHover }}
                  onClick={handleEditNote}
                  clickable={isEditable || isNoteEditable}
                  placeholderContent={noteHoverContent}
                  showPlaceholder={!hasInternalNote}
                >
                  <Typography
                    variant={'body1'}
                    className={classnames(classes.internalNote, 'crew-note-highlight')}
                    component={'div'}
                  >
                    {hasInternalNote &&
                      (translating && !translatedInternalNote ? (
                        <>
                          <Skeleton height={16} width="85%" style={{ marginBottom: 6 }} />
                          <Skeleton height={16} width="70%" style={{ marginBottom: 6 }} />
                          <Skeleton height={16} width="50%" />
                        </>
                      ) : (
                        <HtmlContent content={translatedInternalNote} />
                      ))}
                  </Typography>
                </QuoteHeaderCard>
              )}
            </Grid>

            <Grid item={true} xs={12} sm={4} className={classnames('hours-card', classes.avoidPageBreak)}>
              {showTotalHours && (
                <QuoteHeaderCard
                  title={__('Total Hours')}
                  classes={{ contents: classes.hours }}
                  clickable={isEditable}
                  onClick={handleEditNote}
                >
                  {details.totals.hours.useCustom ? (
                    <CustomBadge showBadge={isEditable}>
                      <Typography variant={'h2'} format={'hours'}>
                        {localizeDecimal(details.totals.hours.custom.toString(), lang)}
                      </Typography>
                    </CustomBadge>
                  ) : (
                    <Typography
                      variant={'h2'}
                      value={
                        typeof details.totals.hours.default === 'string'
                          ? Number.parseFloat(details.totals.hours.default)
                          : details.totals.hours.default
                      }
                      format={'hours'}
                    />
                  )}
                </QuoteHeaderCard>
              )}
            </Grid>

            <Grid item={true} xs={12} sm={8} className={classes.avoidPageBreak}>
              {(!!workOrderProductNote || isEditable) && (
                <QuoteHeaderCard
                  title={__('Product Description')}
                  classes={{ contents: classes.note, placeholderContent: classes.noteHover }}
                  onClick={handleEditNote}
                  clickable={isEditable}
                  placeholderContent={productHoverContent}
                  showPlaceholder={!workOrderProductNote}
                >
                  <Typography variant={'body1'} component={'div'} className={classes.internalNote}>
                    {translating && !translatedWorkOrderProductNote ? (
                      <>
                        <Skeleton height={16} width="85%" style={{ marginBottom: 6 }} />
                        <Skeleton height={16} width="70%" style={{ marginBottom: 6 }} />
                        <Skeleton height={16} width="50%" />
                      </>
                    ) : (
                      <HtmlContent content={translatedWorkOrderProductNote} />
                    )}
                  </Typography>
                </QuoteHeaderCard>
              )}
            </Grid>
            <Grid item={true} xs={12} sm={4} className={classes.avoidPageBreak} classes={{ root: 'total-dimensions' }}>
              {hasTotalDimensions && (
                <QuoteHeaderCard
                  title={`${__('Total Dimensions')} (${objectLabels.unit.abbreviation.square})`}
                  clickable={false}
                >
                  <Grid container={true}>
                    {totals.dimensions.sqftWalls > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography component="span">{__('Walls:')} </Typography>
                        <Typography component="span" value={totals.dimensions.sqftWalls} />
                      </Grid>
                    )}
                    {totals.dimensions.sqftCeiling > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography component="span">{__('Ceiling:')} </Typography>
                        <Typography component="span" value={totals.dimensions.sqftCeiling} />
                      </Grid>
                    )}
                    {totals.dimensions.sqftFloor > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography component="span">{__('Floor:')} </Typography>
                        <Typography component="span" value={totals.dimensions.sqftFloor} />
                      </Grid>
                    )}
                    {totals.dimensions.sqft > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography component="span">{__('Other:')} </Typography>
                        <Typography component="span" value={totals.dimensions.sqft} />
                      </Grid>
                    )}
                    {totals.dimensions.lnft > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography component="span">{objectLabels.unit.abbreviation.linear}: </Typography>
                        <Typography component="span" value={totals.dimensions.lnft} />
                      </Grid>
                    )}
                  </Grid>
                </QuoteHeaderCard>
              )}
            </Grid>
          </Grid>
        </>
      )}

      {!hideBidTables && (
        <div className={classnames({ [classes.hidePrint]: !showWorkOrderAreas })}>
          <WorkOrderTable
            classes={{ root: classes.table }}
            section={'bid'}
            tableView={'area'}
            title={__('Areas')}
            collapsed={!showWorkOrderAreas}
            onReorder={handleReorder}
            onAddClick={handleAdd}
            onItemClick={(ev, key) => handleItemActionClick(ev, 'edit', 'bid', key)}
            showAddButton={false}
            onToggle={(ev) => handleToggle(ev, 'showWorkOrderAreas')}
            showHours={showWorkOrderAreaValues}
            showTotalHours={showWorkOrderAreaValues}
            onToggleAmount={handleToggleAmount}
            showEmpty={true}
            showLineItems={true}
          />
        </div>
      )}

      {!hideAdditionalWork && showWorkOrderAreas && (
        <div className={classnames({ [classes.hidePrint]: !showWorkOrderAreas })}>
          <WorkOrderTable
            classes={{ root: classes.table }}
            section={'additional'}
            tableView={'area'}
            title={__('Additional Work')}
            onReorder={handleReorder}
            onAddClick={handleAdd}
            onItemClick={(ev, key) => handleItemActionClick(ev, 'edit', 'additional', key)}
            showAddButton={false}
            showTotalHours={showWorkOrderAreaValues}
            showHours={showWorkOrderAreaValues}
            showEmpty={false}
            showLineItems={true}
          />
        </div>
      )}

      {!hideBidTables && (
        <div className={classnames({ [classes.hidePrint]: !showWorkOrderSubstrates })}>
          <WorkOrderTable
            classes={{ root: classes.table }}
            section={'bid'}
            tableView={'substrate'}
            title={objectLabels.substrate.plural}
            collapsed={!showWorkOrderSubstrates}
            onReorder={handleReorder}
            onAddClick={handleAdd}
            onItemClick={(ev, key) => handleItemActionClick(ev, 'edit', 'bid', key)}
            onToggle={(ev) => handleToggle(ev, 'showWorkOrderSubstrates')}
            showHours={showWorkOrderSubstrateValues}
            showTotalHours={showWorkOrderAreaValues}
            showLineItems={!showWorkOrderAreas}
            showEmpty={true}
          />
        </div>
      )}
      {!hideAdditionalWork && showWorkOrderSubstrates && (
        <div className={classnames({ [classes.hidePrint]: !showWorkOrderSubstrates })}>
          <WorkOrderTable
            classes={{ root: classes.table }}
            section={'additional'}
            tableView={'substrate'}
            title={__('Additional Work')}
            onReorder={handleReorder}
            onAddClick={handleAdd}
            onItemClick={(ev, key) => handleItemActionClick(ev, 'edit', 'additional', key)}
            showAddButton={false}
            showHours={showWorkOrderSubstrateValues}
            showTotalHours={showWorkOrderAreaValues}
            showEmpty={false}
            showLineItems={true}
          />
        </div>
      )}

      {!hideOptions && (
        <div
          className={classnames({
            [classes.optionsTable]: true,
            [classes.hidePrint]: !showWorkOrderAreaOptions
          })}
        >
          {workOrderOptionsConsumer === 'customer' ? (
            <OptionsTable
              collapsed={!showWorkOrderAreaOptions}
              collapsedIfEmpty
              onToggle={(ev) => handleToggle(ev, 'showWorkOrderAreaOptions')}
              onItemAction={(action, section, item) => handleItemActionClick(null, action, section, item.key)}
              consumer={'customer'}
              showValues={showWorkOrderAreaValues}
              rightContent={optionsViewSwitcher}
              inWorkOrder
            />
          ) : (
            <WorkOrderTable
              classes={{ root: classes.table }}
              section={'options'}
              tableView={'area'}
              rightContent={optionsViewSwitcher}
              title={__('Options')}
              subTitle={__('These items are optional additions and are not included in the total')}
              collapsed={!showWorkOrderAreaOptions}
              onReorder={handleReorder}
              onAddClick={handleAdd}
              onItemClick={(ev, key) => handleItemActionClick(ev, 'edit', 'options', key)}
              showAddButton={false}
              onToggle={(ev) => handleToggle(ev, 'showWorkOrderAreaOptions')}
              showTotalHours={showWorkOrderAreaValues}
              showHours={showWorkOrderAreaValues}
              onToggleAmount={handleToggleAmount}
              showEmpty={true}
              showLineItems={true}
            />
          )}
        </div>
      )}

      <div id={'pictures'} className={classnames({ [classes.hidePrint]: !showWorkOrderFiles })}>
        <FilesByItem
          FormSectionTitle={
            <FormSectionTitle
              title={__('Media')}
              showToggle={isEditable}
              toggleValue={showWorkOrderFiles}
              style={{ marginBottom: 0 }}
              onToggle={(ev) => handleToggle(ev, 'showWorkOrderFiles')}
            />
          }
          sections={['bid', 'additional', 'added-options', 'pending', 'options']}
          consumer={'crew'}
        />
      </div>
    </div>
  )
}

const getProductNote = ({
  quote,
  options,
  details
}: {
  quote: QuoteDocument
  options: OptionsDocument
  details: QuoteDetails
}) => {
  const productNote = details.notes.products.useCustom
    ? (details.notes.products.custom as string)
    : (details.notes.products.default as string)
  // We want additional work and added options products to be in product description too. Lets add them to the end with a note
  // Honours an estimator's Additional Work override (details.notes.additionalProducts) so this view
  // shows the same text the redesigned work order does.
  const additionalWorkNote = details.notes.additionalProducts?.useCustom
    ? (details.notes.additionalProducts.custom as string)
    : (details.notes.additionalProducts?.default as string) ??
      generateProductDescription({ quote, options, section: 'additional' })
  let workOrderProductNote = productNote
  if (additionalWorkNote.length > 0) {
    workOrderProductNote =
      workOrderProductNote +
      `${workOrderProductNote.length ? '\n' : ''}<b>Additional Work Products:</b>` +
      '\n' +
      additionalWorkNote
  }
  return workOrderProductNote
}

export default WorkOrderView
